import type { Ordnerwahl } from './Importordner'

/**
 * **Vom Datenimport zu seinem Beleg — und zurück** (5.1 Abschnitt 3.61,
 * R1245–R1247).
 *
 * Die Adresse trägt alles mit (R156): welcher Beleg, in welcher Gesellschaft
 * und welchem Ordner er liegt, und wie die Liste aussah, aus der gesprungen
 * wurde. Ist der Sprung angekommen, nimmt die Seite die Angaben wieder aus der
 * Adresse — wie beim *Aktualisieren* aus dem Konzernabschluss-Monitor (R880).
 */

/** Wie die Liste der Datenimporte aussah: Filter und links gewählter Ordner. */
export type Listenstand = {
  eingrenzung: { periode: string; einheit: string; schicht: string }
  ordnerwahl: Ordnerwahl
}

/** Wohin gesprungen wird. */
export type Belegziel = { belegId: number; einheit: string; ordnerId: number | null }

/** Die Adresse des Sprungs ins Buchungsjournal (R1245). */
export function belegsprung(ziel: Belegziel, stand: Listenstand): string {
  const frage = new URLSearchParams({
    beleg: String(ziel.belegId),
    einheit: ziel.einheit,
    zurueck: JSON.stringify(stand),
  })
  if (ziel.ordnerId !== null) {
    frage.set('ordner', String(ziel.ordnerId))
  }
  return `#/nachweis/buchungsjournal?${frage.toString()}`
}

/** Was die Adresse über den Sprung sagt — `null`, wenn sie keinen trägt. */
export function belegzielLesen(ziel: Record<string, string>): Belegziel | null {
  const belegId = Number(ziel.beleg)
  if (!Number.isInteger(belegId) || belegId <= 0 || !ziel.einheit) {
    return null
  }
  const ordner = Number(ziel.ordner)
  return { belegId, einheit: ziel.einheit, ordnerId: Number.isInteger(ordner) && ordner > 0 ? ordner : null }
}

/** Die Adresse des Rückwegs zu den Datenimporten (R1246). */
export function rueckweg(stand: string): string {
  return `#/datenimport/lieferungen?${new URLSearchParams({ liste: stand }).toString()}`
}

/**
 * Den Stand der Liste aus der Adresse lesen — `null`, wenn keiner dabei ist
 * oder er nicht zu lesen ist. Eine verdorbene Adresse soll die Liste nicht
 * umwerfen: Dann steht sie wie beim ersten Öffnen da.
 */
export function listenstandLesen(text: string | undefined): Listenstand | null {
  if (!text) {
    return null
  }
  try {
    const roh = JSON.parse(text) as Partial<Listenstand>
    const e = roh.eingrenzung
    const o = roh.ordnerwahl
    if (!e || typeof e.periode !== 'string' || typeof e.einheit !== 'string' || typeof e.schicht !== 'string') {
      return null
    }
    const ordnerwahl: Ordnerwahl =
      o?.art === 'gesellschaft' && typeof o.einheit === 'string'
        ? { art: 'gesellschaft', einheit: o.einheit }
        : o?.art === 'ordner' && typeof o.einheit === 'string' && typeof o.id === 'number'
          ? { art: 'ordner', einheit: o.einheit, id: o.id }
          : { art: 'alle' }
    return { eingrenzung: { periode: e.periode, einheit: e.einheit, schicht: e.schicht }, ordnerwahl }
  } catch {
    return null
  }
}
